import { cn } from "@/lib/utils";

export type TabItem<T extends string> = { value: T; label: string };

/**
 * A segmented tab strip: one row of pills, one panel below, only the active panel mounted.
 *
 * Extracted from the analytics aggregates panel once the team-member profile needed the same
 * thing (2026-08-25) — a second hand-rolled `role="tablist"` is how two surfaces start
 * disagreeing about what a selected tab looks like, and about whether the inactive panel is
 * still in the accessibility tree.
 *
 * **Only the active panel is rendered**, deliberately. An inactive panel left mounted puts a
 * second copy of the same figures into the accessibility tree, and a screen reader has no way
 * to know which of the two the sighted reader is looking at.
 *
 * Selection is the caller's state, not this component's: on the pipeline it lives in the URL
 * (a shared link opens on the tab it names), on a profile it is component state. Both are
 * correct for their surface, so neither is baked in here.
 */
export function Tabs<T extends string>({
  label,
  tabs,
  active,
  onChange,
  idPrefix,
  children,
}: {
  /** Names the tablist for a screen reader — the group's purpose, not the active tab. */
  label: string;
  tabs: readonly TabItem<T>[];
  active: T;
  onChange: (value: T) => void;
  /** Namespaces the generated ids, so two tab strips on one page cannot collide. */
  idPrefix: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <>
      <div role="tablist" aria-label={label} className="bg-muted mb-4 inline-flex rounded-md p-0.5">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.value}`}
            aria-selected={active === tab.value}
            aria-controls={`${idPrefix}-panel-${tab.value}`}
            onClick={() => onChange(tab.value)}
            className={cn(
              "focus-visible:ring-ring rounded-[5px] px-3 py-1 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none",
              active === tab.value
                ? "bg-background text-foreground font-medium shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`${idPrefix}-panel-${active}`} aria-labelledby={`${idPrefix}-tab-${active}`}>
        {children}
      </div>
    </>
  );
}
